import './bookable-selection.scss'

import { DateTime } from 'luxon'
import { ToggleGroup } from 'radix-ui'
import { useState } from 'react'

import { PlayerDataServiceAPI } from '@API/data-services/player-data-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'
import { Button } from '@Components/button/button'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { useQuery } from '@tanstack/react-query'

import { WeekDatePicker } from './date-picker'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'
import { useHostConfig } from '../../../../hooks/use-host-config'
import { ClaimCenterBookableContainer } from './bookable-selection-container'

const CLASS_NAME = 'bookable-selection-screen'

export type SlotSelectionProps = {
  selectedCenters: string[]
  numberOfTickets: number
  onSubmit: (slot: string) => void
  onBack: () => void
}

const toISODateString = (date: DateTime<boolean>) => {
  return date.toISO()?.substring(0, 10)
}

export const SlotSelection = ({
  onSubmit,
  onBack,
  numberOfTickets,
  selectedCenters,
}: SlotSelectionProps) => {
  const [bookable, setBookable] = useState<string | null>(null)
  const [activeDate, setActiveDate] = useState<DateTime>(DateTime.now())
  const hostConfig = useHostConfig()

  const { data: availability, isLoading } = useQuery({
    queryKey: [
      `claim-centers-availability-${numberOfTickets}-${activeDate}-${selectedCenters.join('-')}`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return (
        await PlayerDataServiceAPI.getClaimCentersAvailability(
          toISODateString(activeDate)!,
          selectedCenters,
          numberOfTickets,
        )
      ).sort((a, b) => a.claimCenter.name.localeCompare(b.claimCenter.name))
    },
  })

  return (
    <section className={CLASS_NAME}>
      <p>{hostConfig.bookableScreen.subHeader}</p>

      <WeekDatePicker
        initialDateISOString={toISODateString(activeDate) ?? ''}
        onDateChange={setActiveDate}
      />

      <LoadingIndicator active={isLoading} />

      <ToggleGroup.Root
        type="single"
        value={bookable ?? ''}
        onValueChange={setBookable}
        aria-label="Select a time slot"
        role="radiogroup">
        {availability?.map((availability) => {
          return (
            <ClaimCenterBookableContainer
              key={`${availability.claimCenter.id}-section`}
              claimCenterName={availability.claimCenter.name}
              slots={availability.timeslots}
              selectedSlotId={bookable ?? undefined}
            />
          )
        })}
      </ToggleGroup.Root>

      <FormFloatingFooter>
        <section className={`floating-footer__buttons`}>
          <Button
            className={`floating-footer__next-button`}
            disabled={!bookable}
            onClick={() => (bookable ? onSubmit(bookable) : null)}>
            Next
          </Button>
          <Button
            className={`floating-footer__previous-button`}
            onClick={onBack}
            aria-label="Go back to previous step">
            <img src={PreviousChevron} alt="" aria-hidden="true" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
